import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Users, Calendar, Newspaper, Image as ImageIcon, CreditCard, TrendingUp, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, sub, tint = "primary" }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-serif font-bold">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-md bg-${tint}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${tint}`} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/admin/stats").then((r) => setS(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  if (error) return <div className="text-destructive">{error}</div>;
  if (!s)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-serif font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Welcome back. Here's what's happening on the site.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Members" value={s.members_total} sub={`${s.members_pending} pending`} />
        <StatCard icon={Calendar} label="Events" value={s.events_total} />
        <StatCard icon={Newspaper} label="News posts" value={s.news_total} sub={`${s.news_published} published`} />
        <StatCard icon={ImageIcon} label="Gallery items" value={s.gallery_total} />
        <StatCard icon={CreditCard} label="Payments logged" value={s.payments_total} />
        <StatCard
          icon={TrendingUp}
          label="Total revenue (paid)"
          value={`₦${Number(s.revenue_paid || 0).toLocaleString()}`}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif font-semibold">Recent members</h2>
            <Link to="/admin/members" className="text-xs text-primary hover:underline">
              View all →
            </Link>
          </div>
          {s.recent_members.length === 0 ? (
            <div className="text-sm text-muted-foreground">No members yet.</div>
          ) : (
            <ul className="divide-y">
              {s.recent_members.map((m) => (
                <li key={m.id} className="py-2 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{m.name}</div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </div>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      m.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : m.status === "pending"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {m.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif font-semibold">Recent payments</h2>
            <Link to="/admin/payments" className="text-xs text-primary hover:underline">
              View all →
            </Link>
          </div>
          {s.recent_payments.length === 0 ? (
            <div className="text-sm text-muted-foreground">No payments yet.</div>
          ) : (
            <ul className="divide-y">
              {s.recent_payments.map((p) => (
                <li key={p.id} className="py-2 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{p.member_name}</div>
                    <div className="text-xs text-muted-foreground">{p.purpose}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">
                      {p.currency} {Number(p.amount).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{p.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
